import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ContainerOutlined,
  EyeOutlined,
  NumberOutlined,
  ReloadOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import axios from "../../services/axiosInstance";
import {
  COMPLETE_RAKE,
  GET_ACTIVE_RAKE,
} from "../../services/apiEndPoints";
import { IActiveRake } from "../types/rake.types";

const { Text, Title } = Typography;

interface ActiveRakeCardProps {
  compact?: boolean;
  onChange?: () => void;
}

const ActiveRakeCard = ({ compact = false, onChange }: ActiveRakeCardProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [active, setActive] = useState<IActiveRake | null>(null);

  const fetchActive = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(GET_ACTIVE_RAKE);
      const payload = res?.data;
      if (payload?.active && payload?.data) {
        setActive(payload.data as IActiveRake);
      } else {
        setActive(null);
      }
    } catch {
      setActive(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActive();
  }, [fetchActive]);

  const totals = useMemo(() => {
    const details = active?.rakeDetails ?? [];
    return details.reduce(
      (acc, r) => {
        acc.actualBags += Number(r.actualBags || 0);
        acc.rrBags += Number(r.rrBags || 0);
        acc.chargeWeight += Number(r.chargeWeight || 0);
        acc.actualWeight += Number(r.actualWeight || 0);
        return acc;
      },
      { actualBags: 0, rrBags: 0, chargeWeight: 0, actualWeight: 0 }
    );
  }, [active]);

  const handleComplete = useCallback(() => {
    if (!active?.rakeMaster?.rakeMasterId) return;
    Modal.confirm({
      title: "Complete this rake operation?",
      content:
        "Marking this rake as complete will close it for further weightment entries. This cannot be undone.",
      okText: "Yes, complete",
      okButtonProps: { type: "primary" },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setCompleting(true);
          await axios.post(COMPLETE_RAKE(active.rakeMaster.rakeMasterId));
          message.success("Rake operation completed");
          await fetchActive();
          onChange?.();
        } catch (error: any) {
          const status = error?.response?.status;
          if (status === 404) {
            message.error("Active rake not found");
          } else {
            message.error("Failed to complete rake");
          }
        } finally {
          setCompleting(false);
        }
      },
    });
  }, [active, fetchActive, onChange]);

  if (loading) {
    return (
      <Card
        className="content-card"
        style={{ borderRadius: 14, marginBottom: compact ? 16 : 20 }}
        styles={{ body: { padding: 24, textAlign: "center" } }}
      >
        <Spin />
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Checking for active rake operation...
          </Text>
        </div>
      </Card>
    );
  }

  if (!active) {
    return (
      <Card
        className="content-card"
        style={{
          borderRadius: 14,
          marginBottom: compact ? 16 : 20,
          border: "1px dashed #cbd5e1",
          background: "#f8fafc",
        }}
        styles={{ body: { padding: 24 } }}
      >
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space size={12} align="center">
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "#e2e8f0",
                  color: "#64748b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                }}
              >
                <ContainerOutlined />
              </div>
              <div>
                <Text strong style={{ fontSize: 15, display: "block" }}>
                  No active rake operation
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Upload a new rake to start an operation
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchActive}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>
    );
  }

  const { rakeMaster } = active;

  return (
    <Card
      className="content-card"
      style={{
        borderRadius: 14,
        marginBottom: compact ? 16 : 20,
        border: "1px solid #bbf7d0",
        background:
          "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 60%, #ffffff 100%)",
      }}
      styles={{ body: { padding: compact ? 18 : 22 } }}
    >
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col xs={24} md={14}>
          <Space size={14} align="start">
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: "linear-gradient(135deg, #10b981, #34d399)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
                boxShadow: "0 4px 12px rgba(16,185,129,0.30)",
                flexShrink: 0,
              }}
            >
              <ThunderboltOutlined />
            </div>
            <div style={{ minWidth: 0 }}>
              <Space size={8} align="center" wrap>
                <Tag
                  icon={<SyncOutlined spin />}
                  color="success"
                  style={{ margin: 0, borderRadius: 6, fontWeight: 600 }}
                >
                  Active rake operation
                </Tag>
                {rakeMaster.company && (
                  <Tag
                    icon={<BankOutlined />}
                    color="purple"
                    style={{ margin: 0, borderRadius: 6 }}
                  >
                    {rakeMaster.company}
                  </Tag>
                )}
              </Space>
              {compact ? (
                <Text
                  strong
                  style={{
                    fontSize: 16,
                    display: "block",
                    marginTop: 6,
                    color: "#0f172a",
                  }}
                >
                  Rake {rakeMaster.rrNumber || rakeMaster.rakeMasterId}
                </Text>
              ) : (
                <Title
                  level={4}
                  style={{ margin: "6px 0 2px", color: "#0f172a" }}
                >
                  Rake {rakeMaster.rrNumber || rakeMaster.rakeMasterId}
                </Title>
              )}
              <Space size={12} wrap>
                {rakeMaster.fnrNumber && (
                  <Text style={{ fontSize: 12, color: "#475569" }}>
                    <NumberOutlined style={{ marginRight: 4 }} />
                    FNR {rakeMaster.fnrNumber}
                  </Text>
                )}
                {rakeMaster.date && (
                  <Text style={{ fontSize: 12, color: "#475569" }}>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {moment(rakeMaster.date).format("MMM D, YYYY")}
                  </Text>
                )}
                <Text style={{ fontSize: 12, color: "#475569" }}>
                  Started {moment(rakeMaster.createdAt).fromNow()}
                </Text>
              </Space>
            </div>
          </Space>
        </Col>

        <Col xs={24} md={10}>
          <Row gutter={[8, 8]}>
            <Col xs={12}>
              <StatPill label="Wagons" value={active.rakeDetails.length} />
            </Col>
            <Col xs={12}>
              <StatPill
                label="Actual Bags"
                value={totals.actualBags.toLocaleString()}
              />
            </Col>
            <Col xs={12}>
              <StatPill
                label="Actual Weight"
                value={`${totals.actualWeight.toLocaleString()} kg`}
              />
            </Col>
            <Col xs={12}>
              <StatPill
                label="Charge Weight"
                value={`${totals.chargeWeight.toLocaleString()} kg`}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row
        justify="end"
        align="middle"
        style={{ marginTop: 16 }}
        gutter={[8, 8]}
      >
        <Col>
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchActive}
            />
          </Tooltip>
        </Col>
        <Col>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate("/live")}
          >
            View live operation
          </Button>
        </Col>
        <Col>
          <Button
            size="small"
            onClick={() => navigate(`/rakes/${rakeMaster.rakeMasterId}`)}
          >
            Open rake
          </Button>
        </Col>
        <Col>
          <Button
            size="small"
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={completing}
            onClick={handleComplete}
          >
            Complete operation
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

const StatPill = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div
    style={{
      background: "rgba(255,255,255,0.75)",
      border: "1px solid #d1fae5",
      borderRadius: 10,
      padding: "8px 12px",
    }}
  >
    <Text
      style={{
        display: "block",
        fontSize: 11,
        color: "#64748b",
        fontWeight: 500,
        lineHeight: 1.2,
      }}
    >
      {label}
    </Text>
    <Text
      strong
      style={{
        fontSize: 15,
        color: "#0f172a",
        lineHeight: 1.2,
      }}
    >
      {value}
    </Text>
  </div>
);

export default ActiveRakeCard;
